import React, {
  Suspense,
  useEffect,
  useState,
} from 'react';
import classNames from 'classnames';
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, Paper, TextField,
  Typography,
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';
import { Helmet } from 'react-helmet';
import { ConnectedRouter } from 'connected-react-router';
import { Redirect } from 'react-router-dom';
import { history } from '../../../core';
import styles from './Login.styles';
import { LoginDispatchFunc, LoginInfo, RequestLoginArgs } from './types';
import config from '../../../config';
import NetworkState from '../../../core/types/network';

import ProgressMask from '../../../components/progressMask';
import routes from '../main/routes';
import { MainDispatchFunc, MessageBoxInfo } from '../main/types';

const useStyles = makeStyles(styles);

type Props = {
  className?: string;
  title?: string;
  initialize: LoginDispatchFunc<unknown>;
  loginNetworkStates: {
    loginRequest: NetworkState;
  };
  requestLogin: LoginDispatchFunc<RequestLoginArgs>;
  loginStatus?: LoginInfo;
  messageBoxInfo?: MessageBoxInfo;
  closeMessageBox: MainDispatchFunc;
  isMessageBoxOpen: boolean;
};

const Login: React.FC<Props> = ({
  className,
  title,
  initialize,
  loginNetworkStates: {
    loginRequest,
  },
  requestLogin,
  loginStatus,
}: Props) => {
  const classes = useStyles();
  const [usernameInput, setUsernameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [rememberCheck, setRememberCheck] = useState(false);

  const canSignIn = () => {
    if (usernameInput === '' || passwordInput === '') {
      return true;
    }

    if (usernameInput.length < 4 || passwordInput.length < 4) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <Box className={classNames(classes.root, className)}>
      {loginStatus?.status === 'success' && (
        <ConnectedRouter history={history}>
          <Suspense
            fallback={(
              <Box className={classes.progressContainer}>
                <ProgressMask />
              </Box>
            )}
          >
            <Redirect to={routes.result} />
          </Suspense>
        </ConnectedRouter>
      )}
      <Helmet>
        <title>
          {title || 'Login'}
        </title>
      </Helmet>

      <Paper classes={{ root: classes.loginBody }} square>
        <Box className={classes.logoContainer}>
          <img
            src={`${config.routerBase}/logo.png`}
            alt="Lab Connect "
            className={classes.logo}
          />
        </Box>
        <TextField
          label="Username"
          variant="outlined"
          size="medium"
          required
          className={classes.formControl}
          value={usernameInput}
          onChange={(event) => setUsernameInput(event.target.value)}
          disabled={loginRequest.isRequesting}
        />
        <TextField
          label="Password"
          variant="outlined"
          size="medium"
          required
          type="password"
          className={classes.formControl}
          value={passwordInput}
          onChange={(event) => setPasswordInput(event.target.value)}
          onKeyDown={(key) => key.key === 'Enter' && requestLogin({
            username: usernameInput,
            password: passwordInput,
            remember: rememberCheck,
          })}
          disabled={loginRequest.isRequesting}
        />
        <FormControlLabel
          className={classes.formControlLabel}
          control={(
            <Checkbox
              value="remember"
              color="primary"
              checked={rememberCheck}
              onChange={(event) => setRememberCheck(event.target.checked)}
            />
          )}
          label="Remember me"
          disabled={loginRequest.isRequesting}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          size="large"
          className={classes.formControl}
          disabled={canSignIn() || loginRequest.isRequesting}
          onClick={() => requestLogin({
            username: usernameInput,
            password: passwordInput,
            remember: rememberCheck,
          })}
        >
          {loginRequest.isRequesting ? (
            <CircularProgress size={24} className={classes.buttonProgress} />
          ) : 'Sign In'}
        </Button>
      </Paper>

      <Box>
        <Typography variant="body2" className={classes.version}>
          Versions : 1.24.03
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
